import { useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, Input } from '@zillow/constellation-10';

import { AppSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import accessUtils from '../../utils/accessUtils';

import type { ChangeEvent, Dispatch, KeyboardEvent, SetStateAction } from 'react';
import type { ErrorRMWProps } from '../../model/ErrorRMW';

const KEY = {
  ENTER: 'Enter',
  BACKSPACE: 'Backspace',
  e: 'e',
  E: 'E',
} as const;

const finalizeValue = ({ value, length }: { value: Array<string>; length: number }) => {
  const valueToJoin = value.length <= length ? value : value.slice(0, length);

  return valueToJoin.join('');
};

const validateInput = (value: string) => {
  const numericValue = parseInt(value, 10);
  if ((numericValue >= 0 && numericValue <= 9) || value === '') {
    return true;
  }
  return false;
};

type EventProps = ChangeEvent<HTMLInputElement> & KeyboardEvent;

type Props = {
  clearErrors: Dispatch<SetStateAction<Partial<ErrorRMWProps> | undefined>>;
  codeLength: number;
  onEnterKeyPress: () => void;
  onComplete: Dispatch<SetStateAction<string>>;
};

const VerificationCodeValidator = ({ clearErrors, codeLength, onEnterKeyPress, onComplete }: Props): JSX.Element => {
  const isMobile = useSelector((state) => AppSelectors.makeIsMobile(state));
  const { cookieEnabledServices } = useSelector(
    (state) => ({ cookieEnabledServices: state?.app?.cookieEnabledServices || {} }),
    shallowEqual,
  );

  const [verificationCodeMultiInput, setVerificationCodeMultiInput] = useState<Array<string>>(
    new Array(codeLength).fill(''),
  );
  const [verificationCodeSingleInput, setVerificationCodeSingleInput] = useState('');

  const pasteRef = useRef('');
  const inputRefs = useRef<Array<HTMLInputElement>>([]);
  const singleInputRef = useRef<HTMLInputElement>(null);

  const canAccessMultiBoxVerificationInput = accessUtils.canAccessMultiBoxVerificationInput({ cookieEnabledServices });

  useEffect(() => {
    inputRefs?.current[0]?.focus();
    singleInputRef?.current?.focus();
  }, []);

  const isVerificationCodeComplete = (inputtedCode: Array<string>) => {
    const emptyValues = inputtedCode.filter((num) => num === '');

    return emptyValues.length === 0;
  };

  const handleDelete = (index: number) => {
    const verificationCodeCopy = [...verificationCodeMultiInput];
    verificationCodeCopy[index] = '';
    setVerificationCodeMultiInput(verificationCodeCopy);

    // NOTE: prevent the case where user entered up to the end then backspace and submit
    if (verificationCodeCopy[verificationCodeCopy.length - 2]) {
      onComplete(finalizeValue({ value: verificationCodeCopy, length: codeLength }));
    }

    if (index - 1 >= 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleOnEnterKeyPress = (key: string) => {
    const isComplete = isVerificationCodeComplete(verificationCodeMultiInput);
    if (isComplete) {
      onComplete(finalizeValue({ value: verificationCodeMultiInput, length: codeLength }));
      if (key === KEY.ENTER) {
        onEnterKeyPress();
      }
    }
  };

  const getKey = (e: EventProps): Promise<string> =>
    new Promise((resolve) => {
      const { target, key: pKey } = e;

      setTimeout(() => {
        // on some devices the key value is returned as "Unidentified"
        const key = pKey === 'Unidentified' ? target.value : pKey;

        resolve(key);
      }, 10);
    });

  const handleKeyDown = async (e: EventProps, index: number) => {
    // onKeyDown fires before onChange
    // get key asynchronously in order to get changed values
    const key = await getKey(e);

    clearErrors({});

    if (key === KEY.BACKSPACE) {
      handleDelete(index);
      return;
    }

    if (key === KEY.ENTER) {
      handleOnEnterKeyPress(key);
    }

    if (pasteRef.current) {
      const combinedPrevAndPasteValue = [
        ...(verificationCodeMultiInput.join('').substring(0, index) + pasteRef.current).substring(0, codeLength),
      ];
      const allValid = combinedPrevAndPasteValue.every((k) => validateInput(k));

      const newVerificationCode = [
        ...combinedPrevAndPasteValue,
        ...Array(codeLength - combinedPrevAndPasteValue.length || 0).fill(''),
      ];
      if (allValid) {
        setVerificationCodeMultiInput(newVerificationCode);
      }

      pasteRef.current = '';

      const isComplete = isVerificationCodeComplete(newVerificationCode);

      if (isComplete) {
        inputRefs.current[codeLength - 1].focus();

        onComplete(finalizeValue({ value: newVerificationCode, length: codeLength }));
      } else {
        inputRefs.current[combinedPrevAndPasteValue.length].focus();
      }
    } else if (validateInput(key)) {
      const verificationCodeCopy = [...verificationCodeMultiInput];
      verificationCodeCopy[index] = key;

      const isLastInputField = index === verificationCodeCopy.length - 1;
      const isComplete = isVerificationCodeComplete(verificationCodeCopy);

      if (isComplete) {
        // still need to set focus when
        if (!isLastInputField && key !== KEY.BACKSPACE) {
          inputRefs.current[index + 1].focus();
        }

        onComplete(finalizeValue({ value: verificationCodeCopy, length: codeLength }));
        return;
      }

      if (key !== KEY.BACKSPACE) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    pasteRef.current = e?.clipboardData?.getData('Text') || '';
  };

  const handleChange = (e: EventProps, index: number) => {
    const key = e.target.value;

    if (!pasteRef.current && validateInput(key)) {
      const verificationCodeCopy = [...verificationCodeMultiInput];
      verificationCodeCopy[index] = key;

      setVerificationCodeMultiInput(verificationCodeCopy);
    }
  };

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleSingleInputOnKeyDown = async (e: EventProps) => {
    try {
      const key = await getKey(e);

      clearErrors({});

      if (key === KEY.ENTER && verificationCodeSingleInput.length === codeLength) {
        onEnterKeyPress();
      }
    } catch (error) {
      console.warn(error);
    }
  };

  const handleSingleInputOnChange = (e: EventProps) => {
    const valueToSubmit = e.target.value.slice(0, codeLength);
    const isValid = [...valueToSubmit].every((char) => validateInput(char));

    if (isValid) {
      setVerificationCodeSingleInput(valueToSubmit);

      if (valueToSubmit.length === codeLength) {
        onComplete(valueToSubmit);
      }
    }
  };

  return (
    <Box
      data-testid="VerificationCodeValidator"
      css={{
        display: 'flex',
        justifyContent: 'center',
        height: '42px',
      }}
    >
      {canAccessMultiBoxVerificationInput ? (
        verificationCodeMultiInput.map((value, i) => (
          <Input
            data-testid="VerificationCodeInput"
            ref={(el: HTMLInputElement) => {
              inputRefs.current[i] = el;
            }}
            key={`verification-code-${i}`}
            value={value}
            onKeyDown={(e: EventProps) => handleKeyDown(e, i)}
            onChange={(e: EventProps) => handleChange(e, i)}
            onBlur={() => handleOnEnterKeyPress('')}
            type={isMobile ? 'number' : 'text'}
            aria-label={`Digit-${i + 1}`}
            css={{
              height: '40px',
              width: '30px',
              marginRight: '4px',
              textAlign: 'center',
              padding: '0',
              letterSpacing: '0.25ch',
              '&:focus': {
                padding: '0',
              },
            }}
            onPaste={handlePaste}
            onFocus={handleOnFocus}
            maxLength={1}
          />
        ))
      ) : (
        <Input
          data-testid="VerificationCodeInput"
          maxLength={codeLength}
          ref={singleInputRef}
          value={verificationCodeSingleInput}
          type={isMobile ? 'number' : 'text'}
          onKeyDown={handleSingleInputOnKeyDown}
          onChange={handleSingleInputOnChange}
          onBlur={() => onComplete(verificationCodeSingleInput)}
          css={{
            height: '40px',
            width: `${2 * codeLength + 4 / codeLength}ch`,
            marginRight: '4px',
            textAlign: 'center',
            padding: '0',
            letterSpacing: '0.25ch',
            '&:focus': {
              padding: '0',
            },
          }}
        />
      )}
    </Box>
  );
};

export { VerificationCodeValidator };
export default VerificationCodeValidator;
